import React, { HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
    variant?: 'flat' | 'elevated' | 'outlined' | 'glass';
    padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
    hover?: boolean;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
    ({ className, variant = 'elevated', padding = 'md', hover = false, children, ...props }, ref) => {
        const baseStyles = `
      rounded-xl
      transition-all duration-200
    `;

        const variantStyles = {
            flat: `
        bg-white dark:bg-[#1e293b]
      `,
            elevated: `
        bg-white dark:bg-[#1e293b]
        shadow-md dark:shadow-lg dark:shadow-black/20
        border border-gray-100 dark:border-gray-700/60
      `,
            outlined: `
        bg-white dark:bg-[#1e293b]
        border border-gray-200 dark:border-gray-700
      `,
            glass: `
        glass backdrop-blur-lg
        border border-white/20 dark:border-white/10
      `,
        };

        const paddingStyles = {
            none: '',
            sm: 'p-3',
            md: 'p-4',
            lg: 'p-6',
            xl: 'p-8',
        };

        const hoverStyles = hover
            ? 'hover:shadow-lg hover:-translate-y-1 cursor-pointer'
            : '';

        return (
            <div
                ref={ref}
                className={cn(
                    baseStyles,
                    variantStyles[variant],
                    paddingStyles[padding],
                    hoverStyles,
                    className
                )}
                {...props}
            >
                {children}
            </div>
        );
    }
);

Card.displayName = 'Card';

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
    divider?: boolean;
}

export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
    ({ className, divider = false, children, ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(
                    'space-y-1.5',
                    divider && 'pb-4 border-b border-gray-200 dark:border-gray-700',
                    className
                )}
                {...props}
            >
                {children}
            </div>
        );
    }
);

CardHeader.displayName = 'CardHeader';

export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
    ({ className, as: Component = 'h3', children, ...props }, ref) => {
        return (
            <Component
                ref={ref as any}
                className={cn(
                    'text-lg font-semibold text-gray-900 dark:text-gray-100',
                    className
                )}
                {...props}
            >
                {children}
            </Component>
        );
    }
);

CardTitle.displayName = 'CardTitle';

export const CardDescription = React.forwardRef<
    HTMLParagraphElement,
    HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
    return (
        <p
            ref={ref}
            className={cn('text-sm text-gray-500 dark:text-gray-400', className)}
            {...props}
        >
            {children}
        </p>
    );
});

CardDescription.displayName = 'CardDescription';

export const CardContent = React.forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
    ({ className, children, ...props }, ref) => {
        return (
            <div ref={ref} className={cn('pt-4', className)} {...props}>
                {children}
            </div>
        );
    }
);

CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
    ({ className, children, ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn('flex items-center gap-2 pt-4', className)}
                {...props}
            >
                {children}
            </div>
        );
    }
);

CardFooter.displayName = 'CardFooter';

export default Card;
